import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import quickbooksApi from '../../services/quickbooksApi';
import { useToast } from '../../contexts/ToastContext';
import './QuickBooksIntegrationPage.css';

function formatDateTime(value) {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}

function formatRelativeTime(value) {
  if (!value) return 'never';
  const diffMs = Date.now() - new Date(value).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

export default function QuickBooksIntegrationPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();

  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [monthsBack, setMonthsBack] = useState('');

  const [expenseAccounts, setExpenseAccounts] = useState(null);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [isSavingCogsAccounts, setIsSavingCogsAccounts] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await quickbooksApi.getStatus();
      setStatus(data);
    } catch (err) {
      console.error('Failed to load QuickBooks status:', err);
      showError('Could not load QuickBooks connection status.');
    } finally {
      setIsLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    if (!status?.connected) return;
    quickbooksApi.getExpenseAccounts()
      .then((data) => {
        setExpenseAccounts(data.available_accounts);
        setSelectedAccounts(data.selected_accounts);
      })
      .catch((err) => console.error('Failed to load expense accounts:', err));
  }, [status?.connected]);

  // After the OAuth callback redirects back here with ?connected=true
  useEffect(() => {
    if (searchParams.get('connected') === 'true') {
      showSuccess('QuickBooks connected — running the initial sync.');
      searchParams.delete('connected');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const url = await quickbooksApi.getConnectUrl();
      window.location.href = url;
    } catch (err) {
      console.error('Failed to start QuickBooks connect flow:', err);
      showError('Could not start the QuickBooks connection. Please try again.');
      setIsConnecting(false);
    }
  };

  const handleSyncNow = async () => {
    setIsSyncing(true);
    try {
      const months = monthsBack ? parseInt(monthsBack, 10) : undefined;
      const result = await quickbooksApi.syncNow(months);
      if (result.success) {
        showSuccess(`Synced ${result.invoices_synced} invoice(s) and ${result.bills_synced} bill(s).`);
      } else {
        showError(result.error || 'Sync failed.');
      }
      await fetchStatus();
    } catch (err) {
      console.error('QuickBooks sync failed:', err);
      showError('Sync request failed. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect QuickBooks? Historical synced data is kept, but future syncs will stop until you reconnect.')) {
      return;
    }
    setIsDisconnecting(true);
    try {
      await quickbooksApi.disconnect();
      showSuccess('QuickBooks disconnected.');
      await fetchStatus();
    } catch (err) {
      console.error('Failed to disconnect QuickBooks:', err);
      showError('Could not disconnect QuickBooks. Please try again.');
    } finally {
      setIsDisconnecting(false);
    }
  };

  const toggleAccount = (accountName) => {
    setSelectedAccounts((prev) =>
      prev.includes(accountName) ? prev.filter((a) => a !== accountName) : [...prev, accountName]
    );
  };

  const handleSaveCogsAccounts = async () => {
    setIsSavingCogsAccounts(true);
    try {
      const updated = await quickbooksApi.setCogsAccounts(selectedAccounts);
      setStatus(updated);
      showSuccess('COGS accounts saved — Financial Analysis will pick these up on next load.');
    } catch (err) {
      console.error('Failed to save COGS accounts:', err);
      showError('Could not save COGS accounts. Please try again.');
    } finally {
      setIsSavingCogsAccounts(false);
    }
  };

  if (isLoading) {
    return (
      <div className="qbo-page">
        <div className="qbo-loading">Loading connection status…</div>
      </div>
    );
  }

  return (
    <div className="qbo-page">
      <div className="qbo-header">
        <h1>QuickBooks Online</h1>
        <p>Connect QuickBooks so Financial Analysis reflects real invoices and bills instead of catalog estimates.</p>
      </div>

      {!status?.connected && (
        <div className="qbo-card qbo-not-connected">
          <div className="qbo-status-row">
            <span className="qbo-status-dot qbo-status-dot--off" />
            <span className="qbo-status-label">Not connected</span>
          </div>
          <p className="qbo-description">
            You'll be sent to QuickBooks to sign in and approve access. On approval, an initial
            historical sync runs automatically for the last 12 months (configurable any time from
            here via Sync Now).
          </p>
          <button className="btn-primary" onClick={handleConnect} disabled={isConnecting}>
            {isConnecting ? 'Redirecting…' : 'Connect QuickBooks'}
          </button>
        </div>
      )}

      {status?.connected && (
        <div className="qbo-card qbo-connected">
          <div className="qbo-status-row">
            <span className={`qbo-status-dot ${status.needs_reauth ? 'qbo-status-dot--warn' : 'qbo-status-dot--on'}`} />
            <span className="qbo-status-label">
              {status.needs_reauth ? 'Needs re-authorization' : 'Connected'}
            </span>
          </div>

          {status.needs_reauth && (
            <div className="qbo-banner qbo-banner--warn">
              QuickBooks rejected the stored credentials — reconnect to restore syncing.
            </div>
          )}

          {status.last_sync_error && !status.needs_reauth && (
            <div className="qbo-banner qbo-banner--error">
              Last sync failed: {status.last_sync_error}
            </div>
          )}

          <div className="qbo-meta-grid">
            <div className="qbo-meta-item">
              <span className="qbo-meta-label">Company (realm ID)</span>
              <span className="qbo-meta-value">{status.realm_id}</span>
            </div>
            <div className="qbo-meta-item">
              <span className="qbo-meta-label">Connected since</span>
              <span className="qbo-meta-value">{formatDateTime(status.connected_at)}</span>
            </div>
            <div className="qbo-meta-item">
              <span className="qbo-meta-label">Last synced</span>
              <span className="qbo-meta-value">
                {isSyncing || status.is_syncing ? 'Syncing…' : formatRelativeTime(status.last_synced_at)}
              </span>
            </div>
            <div className="qbo-meta-item">
              <span className="qbo-meta-label">Last sync result</span>
              <span className="qbo-meta-value">
                {status.last_sync_invoices_count == null
                  ? '—'
                  : `${status.last_sync_invoices_count} invoice${status.last_sync_invoices_count === 1 ? '' : 's'}, ${status.last_sync_bills_count} bill${status.last_sync_bills_count === 1 ? '' : 's'}`}
              </span>
              {status.last_sync_payments_count != null && (
                <span className="qbo-meta-subvalue">
                  {status.last_sync_payments_count} payment{status.last_sync_payments_count === 1 ? '' : 's'}, {status.last_sync_bill_payments_count} bill payment{status.last_sync_bill_payments_count === 1 ? '' : 's'}
                </span>
              )}
              {status.last_sync_extra_counts && (
                <span className="qbo-meta-subvalue">
                  {[
                    ['sales_receipts', 'sales receipt'],
                    ['credit_memos', 'credit memo'],
                    ['refund_receipts', 'refund receipt'],
                    ['purchases', 'purchase'],
                    ['vendor_credits', 'vendor credit'],
                  ]
                    .map(([key, label]) => [status.last_sync_extra_counts[key], label])
                    .filter(([count]) => count)
                    .map(([count, label]) => `${count} ${label}${count === 1 ? '' : 's'}`)
                    .join(', ') || 'no other transaction types found'}
                </span>
              )}
            </div>
            <div className="qbo-meta-item">
              <span className="qbo-meta-label">Default lookback window</span>
              <span className="qbo-meta-value">{status.sync_lookback_months} months</span>
            </div>
          </div>

          <div className="qbo-cogs-section">
            <h3>Cost of Goods Sold accounts</h3>
            <p className="qbo-description">
              Cost, gross margin, and net margin stay hidden on Financial Analysis until you confirm
              which of your QuickBooks expense accounts represent COGS — a vendor bill could be rent
              or software just as easily as coffee, so we never guess.
            </p>
            {expenseAccounts === null && <p className="qbo-cogs-loading">Loading synced expense accounts…</p>}
            {expenseAccounts !== null && expenseAccounts.length === 0 && (
              <p className="qbo-cogs-empty">No expense accounts synced yet — run a sync first, then come back here.</p>
            )}
            {expenseAccounts !== null && expenseAccounts.length > 0 && (
              <>
                <div className="qbo-account-list">
                  {expenseAccounts.map((account) => (
                    <label key={account} className="qbo-account-item">
                      <input
                        type="checkbox"
                        checked={selectedAccounts.includes(account)}
                        onChange={() => toggleAccount(account)}
                      />
                      {account}
                    </label>
                  ))}
                </div>
                <button className="btn-secondary" onClick={handleSaveCogsAccounts} disabled={isSavingCogsAccounts}>
                  {isSavingCogsAccounts ? 'Saving…' : 'Save COGS Accounts'}
                </button>
              </>
            )}
          </div>

          <div className="qbo-sync-controls">
            <label htmlFor="qbo-months-back">Resync from further back (optional)</label>
            <div className="qbo-sync-controls-row">
              <input
                id="qbo-months-back"
                type="number"
                min="1"
                max="120"
                placeholder={`${status.sync_lookback_months} (default)`}
                value={monthsBack}
                onChange={(e) => setMonthsBack(e.target.value)}
              />
              <span>months back</span>
              <button className="btn-secondary" onClick={handleSyncNow} disabled={isSyncing || status.is_syncing}>
                {isSyncing || status.is_syncing ? 'Syncing…' : 'Sync Now'}
              </button>
            </div>
          </div>

          <div className="qbo-actions">
            <button className="btn-danger-outline" onClick={handleDisconnect} disabled={isDisconnecting}>
              {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
